
import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, Button, Grid, IconButton, Tooltip } from '@material-ui/core';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import CloseIcon from '@material-ui/icons/Close';
import GetAppIcon from '@material-ui/icons/GetApp';
import { DataGrid } from '@material-ui/data-grid';
import { countOrACVFormatter } from '../../../util/customFunctions';
import { useDispatch } from 'react-redux';

const QuotaDetailsModal = ({
  open, onClose, selectedRep, repQuotaDetails, selectedQuarters, onQuarterToggle, deleteLoading, deleteSuccess, deleteError, onDeleteQuota, classes, isTeamMode = false
}) => {
  const dispatch = useDispatch();
  const [isLoaded, setIsLoaded] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  console.log('QuotaDetailsModal_DEBUG', repQuotaDetails)
  useEffect(() => {
    if (repQuotaDetails && repQuotaDetails.length > 0 && !isLoaded) {
      dispatch({ type: "info_message_generic", payload: { type: "success", message: "Quota Details Loaded" } });
      setIsLoaded(true);
    }

  }, [repQuotaDetails]);

  useEffect(() => {
    if (deleteSuccess) {
      dispatch({ type: "info_message_generic", payload: { type: "success", message: "Quota Details Removed" } });
      onClose();
      setIsLoaded(false)
    }
  }, [deleteSuccess, onClose]);

  const rows = (repQuotaDetails || []).map((row, i) => ({
    id: row.fiscalQuarter || row.fiscal_quarter || String(i),
    fiscalQuarter: row.fiscalQuarter || row.fiscal_quarter || '-',
    quota: countOrACVFormatter(row.quota, true) || '-',
    nqpGoal: countOrACVFormatter(row.nqpGoal, true) || '-',
    rampStatus: row.rampStatus || '-',
  }));

  const columns = [
    { field: 'fiscalQuarter', headerName: 'Fiscal Quarter', width: 120, align: 'left', headerAlign: 'left', renderHeader: (params) => (<Box style={{ whiteSpace: 'normal', wordWrap: 'break-word', lineHeight: '1.2' }}>{params.colDef.headerName}</Box>) },
    { field: 'quota', headerName: 'Quota', width: 120, align: 'right', headerAlign: 'right', renderHeader: (params) => (<Box style={{ whiteSpace: 'normal', wordWrap: 'break-word', lineHeight: '1.2', textAlign: 'right', width: '100%' }}>{params.colDef.headerName}</Box>) },
    { field: 'nqpGoal', headerName: 'NQP Goal', width: 120, align: 'right', headerAlign: 'right', renderHeader: (params) => (<Box style={{ whiteSpace: 'normal', wordWrap: 'break-word', lineHeight: '1.2', textAlign: 'right', width: '100%' }}>{params.colDef.headerName}</Box>) },
    ...(!isTeamMode ? [{ field: 'rampStatus', headerName: 'Ramp Status', width: 120, align: 'left', headerAlign: 'left', renderHeader: (params) => (<Box style={{ whiteSpace: 'normal', wordWrap: 'break-word', lineHeight: '1.2' }}>{params.colDef.headerName}</Box>) }] : []),
  ];

  useEffect(() => {
    if (open && repQuotaDetails && repQuotaDetails.length > 0) {
      const allQuarterIds = rows.map(row => row.id);
      if (selectedQuarters.length === 0 || JSON.stringify([...selectedQuarters].sort()) !== JSON.stringify([...allQuarterIds].sort())) {
        onQuarterToggle(allQuarterIds);
      }
    }
  }, [open, repQuotaDetails]);

  const handleSelectionChange = (newSelection) => {
    if (deleteLoading || deleteSuccess) return;
    onQuarterToggle(newSelection);
  };

  const getSelectedFiscalQuarters = () => {
    return selectedQuarters.map(quarterId => {
      const row = rows.find(r => r.id === quarterId);
      return row ? row.fiscalQuarter : null;
    }).filter(Boolean);
  };

  const handleDeleteClick = () => {
    setConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    const selectedFiscalQuarters = getSelectedFiscalQuarters();
    if (selectedFiscalQuarters.length > 0) {
      onDeleteQuota(selectedFiscalQuarters);
    }
    setConfirmOpen(false);
  };

  const handleCloseConfirmation = () => {
    setConfirmOpen(false);
  };

  const isTableLoading = deleteLoading || !repQuotaDetails || repQuotaDetails.length === 0;

  // CSV Export Function
  const handleExportCSV = () => {
    const headers = columns.map(col => col.headerName).join(',');
    const csvRows = rows.map(row => {
      return columns.map(col => {
        const field = col.field;
        let value = row[field];

        // Escape commas and quotes in CSV
        if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
          value = `"${value.replace(/"/g, '""')}"`;
        }

        return value;
      }).join(',');
    });

    const csvContent = [headers, ...csvRows].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `quota_details_${isTeamMode ? (selectedRep?.team || 'team') : (selectedRep?.fullName || 'rep')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Typography variant="h6" style={{ fontWeight: 600 }}>
                Quota Details for {isTeamMode ? (selectedRep?.team || '-') : (selectedRep?.fullName || '-')}
              </Typography>
              {!isTeamMode && (
                <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
                  Team: {selectedRep?.team || '-'}
                </Typography>
              )}
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Tooltip title="Download as CSV" arrow>
                <IconButton
                  onClick={handleExportCSV}
                  disabled={deleteLoading || rows.length === 0}
                  size="small"
                  style={{
                    padding: '6px',
                    margin: '0 4px',
                  }}
                >
                  <GetAppIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <IconButton
                aria-label="close"
                onClick={onClose}
                disabled={deleteLoading}
                style={{ padding: 4 }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box>
            <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 8 }}>
              Quota Information
            </Typography>
            <div style={{ height: 320, width: '100%' }}>
              <DataGrid
                rows={rows}
                columns={columns}
                checkboxSelection
                disableSelectionOnClick
                disableColumnMenu
                selectionModel={selectedQuarters}
                onSelectionModelChange={handleSelectionChange}
                hideFooter
                loading={isTableLoading}
                sx={{
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: '#4472c4',
                    color: 'white',
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    fontWeight: 600,
                  },
                  '& .MuiDataGrid-sortIcon': {
                    color: 'white',
                  },
                  '& .MuiDataGrid-columnSeparator': {
                    color: 'white',
                  },
                }}
              />
            </div>
          </Box>
        </DialogContent>
        <DialogActions style={{ flexDirection: 'column', alignItems: 'flex-start', padding: 24 }}>
          <Grid container>
            <Grid item xs={4} justifyContent="center" alignItems='center' >
              <Button
                variant="contained"
                color="primary"
                disabled={deleteLoading || selectedQuarters.length === 0}
                onClick={handleDeleteClick}
              >
                Delete quota
              </Button>
            </Grid>
            <Grid item xs={8}>
              <Typography variant="body2" color="textSecondary" style={{ display: 'flex', alignItems: 'center' }}>
                <InfoOutlinedIcon fontSize="medium" style={{ marginRight: '1rem' }} />
                The following quota entries will be deleted for this {isTeamMode ? 'team' : 'rep'}. Please deselect quarters that you don't want to delete. {isTeamMode ? '' : 'If a rep has left the company, deleting quotas for all the quarters after the quarter they leave is recommended.'}
              </Typography>
            </Grid>
          </Grid>
          {deleteError && <Typography color="error" style={{ marginTop: 8 }}>{deleteError}</Typography>}
          {deleteSuccess && <Typography style={{ color: 'green', marginTop: 8 }}>Quota deleted successfully.</Typography>}
        </DialogActions>
      </Dialog>
      <Dialog open={confirmOpen} onClose={handleCloseConfirmation}>
        <DialogTitle>Confirm delete Quota</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete quota entries for <span style={{ fontWeight: 600 }}>{isTeamMode ? (selectedRep?.team || 'this team') : (selectedRep?.fullName || 'this rep')}</span> for the selected fiscal quarters?
          </Typography>
        </DialogContent>
        <DialogActions style={{ justifyContent: 'center' }}>
          <Button onClick={handleConfirmDelete} color="primary" variant="contained">
            Yes
          </Button>
          <Button onClick={handleCloseConfirmation} color="primary" variant="contained">
            No
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default QuotaDetailsModal;